import { useState, type FormEvent } from "react";
import {
  useS3Configs,
  useCreateS3Config,
  useUpdateS3Config,
  useDeleteS3Config,
  useTestS3Connection,
  useS3Migrations,
  useCreateS3Migration,
  useTestS3Compat,
  useTenants,
  type S3Config,
  type ObjectLockMode,
  type CreateS3ConfigInput,
  type S3Migration,
  type S3CompatResult,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import {
  Button,
  Chip,
  ErrorNotice,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
} from "../components/ui";
import { IconPlus } from "../components/icons";

/**
 * Gestión de almacenes S3 (solo super-admin).
 *
 * Cada almacén define las credenciales S3-compatibles para un grupo de tenants.
 * El campo clave es `objectLockMode` — define cómo se aplica la retención WORM
 * (mínimo 1 año por ley DECA):
 *
 *   NONE          → sin retención (panel muestra aviso)
 *   S3_PER_OBJECT → PutObjectRetention por PDF (AWS/MinIO/Storadera/RustFS)
 *   BUCKET_RULE   → retención ya configurada en el bucket (Cloudflare R2)
 *
 * Las credenciales (accessKeyId, secretAccessKey) nunca se devuelven en la
 * lista — solo se pueden escribir al crear o editar.
 */

// ---------------------------------------------------------------------------
// Proveedores predefinidos para autocompletar el formulario
// ---------------------------------------------------------------------------

interface ProviderPreset {
  id: string;
  label: string;
  endpoint: string | null;
  region: string;
  objectLockMode: ObjectLockMode;
  hasVersioning: boolean;
}

const PROVIDERS: ProviderPreset[] = [
  { id: "aws",       label: "Amazon S3",          endpoint: null,                                           region: "eu-west-1",    objectLockMode: "S3_PER_OBJECT", hasVersioning: true  },
  { id: "r2",        label: "Cloudflare R2",       endpoint: "https://<account>.r2.cloudflarestorage.com",  region: "auto",         objectLockMode: "BUCKET_RULE",   hasVersioning: false },
  { id: "storadera", label: "Storadera",            endpoint: "https://s3.storadera.com",                   region: "eu-central-1", objectLockMode: "S3_PER_OBJECT", hasVersioning: true  },
  { id: "rustfs",    label: "RustFS",               endpoint: "",                                            region: "eu-central-1", objectLockMode: "S3_PER_OBJECT", hasVersioning: true  },
  { id: "minio",     label: "MinIO",                endpoint: "",                                            region: "us-east-1",    objectLockMode: "S3_PER_OBJECT", hasVersioning: true  },
  { id: "other",     label: "Otro (S3-compatible)", endpoint: "",                                            region: "us-east-1",    objectLockMode: "NONE",          hasVersioning: false },
];

const OBJECT_LOCK_LABELS: Record<ObjectLockMode, string> = {
  NONE:          "Sin retención",
  S3_PER_OBJECT: "Por objeto (S3 Object Lock)",
  BUCKET_RULE:   "Por bucket (Bucket Lock Rule)",
};

// ---------------------------------------------------------------------------
// Formulario
// ---------------------------------------------------------------------------

interface FormState {
  provider: string;
  name: string;
  endpoint: string;
  region: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
  objectLockMode: ObjectLockMode;
  hasVersioning: boolean;
}

const EMPTY_FORM: FormState = {
  provider: "",
  name: "",
  endpoint: "",
  region: "eu-central-1",
  bucket: "",
  accessKeyId: "",
  secretAccessKey: "",
  objectLockMode: "NONE",
  hasVersioning: false,
};

function formFromConfig(c: S3Config): FormState {
  return {
    provider: "",
    name: c.name,
    endpoint: c.endpoint ?? "",
    region: c.region,
    bucket: c.bucket,
    accessKeyId: "",   // nunca pre-rellenamos credenciales
    secretAccessKey: "",
    objectLockMode: c.objectLockMode,
    hasVersioning: c.hasVersioning,
  };
}

// ---------------------------------------------------------------------------
// Tabla
// ---------------------------------------------------------------------------

const col = createColumnHelper<S3Config>();

// El callback openEdit se pasa desde el componente principal a través de un
// closure en la definición de columnas. Se genera con useMemo allí dentro.
function buildColumns(onEdit: (c: S3Config) => void) {
  return [
    col.accessor("name", { header: "Nombre" }),
    col.accessor("bucket", { header: "Bucket" }),
    col.accessor("region", { header: "Región" }),
    col.accessor("objectLockMode", {
      header: "Retención WORM",
      cell: (info) => {
        const mode = info.getValue();
        const tone = mode === "NONE" ? "warn" : "ok" as const;
        return <Chip tone={tone}>{OBJECT_LOCK_LABELS[mode]}</Chip>;
      },
    }),
    col.accessor("_count", {
      header: "Tenants",
      cell: (info) => info.getValue().tenants,
    }),
    col.accessor("isDefault", {
      header: "Por defecto",
      cell: (info) => (info.getValue() ? <Chip tone="neutral">Sí</Chip> : null),
    }),
    col.display({
      id: "acciones",
      header: "",
      cell: (info) => (
        <Button
          variant="quiet"
          onClick={() => onEdit(info.row.original)}
        >
          Editar
        </Button>
      ),
    }),
  ];
}

// ---------------------------------------------------------------------------
// Wizard de migración S3 (3 pasos)
// ---------------------------------------------------------------------------

type WizardStep = 1 | 2 | 3;

interface MigrationWizardProps {
  configs: S3Config[];
  onClose: () => void;
}

function MigrationWizard({ configs, onClose }: MigrationWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [sourceId, setSourceId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [tenantScope, setTenantScope] = useState<"global" | "tenant">("global");
  const [tenantId, setTenantId] = useState("");
  const [compatResult, setCompatResult] = useState<S3CompatResult | null>(null);
  const [activeMigrationId, setActiveMigrationId] = useState<string | null>(null);

  const testCompatMutation = useTestS3Compat();
  const createMigration = useCreateS3Migration();

  // Lista de tenants para el selector de scope
  const { data: tenantsData } = useTenants();
  const tenants = tenantsData?.items ?? [];

  // Polling de la lista completa — filtramos la migración activa en el render
  const { data: allMigrations = [] } = useS3Migrations();
  const migrationStatus = allMigrations.find((m) => m.id === activeMigrationId) ?? null;

  async function handleTestCompat() {
    setCompatResult(null);
    try {
      const result = await testCompatMutation.mutateAsync({
        sourceId,
        destinationId,
        tenantId: tenantScope === "tenant" && tenantId ? tenantId : null,
      });
      setCompatResult(result);
    } catch (err) {
      setCompatResult({
        ok: false,
        sourceReachable: false,
        destinationReachable: false,
        estimatedObjects: null,
        message: err instanceof Error ? err.message : "Error desconocido",
      });
    }
  }

  async function handleCreate() {
    try {
      const migration = await createMigration.mutateAsync({
        sourceId,
        destinationId,
        tenantId: tenantScope === "tenant" && tenantId ? tenantId : null,
        deleteSource: false,
      });
      setActiveMigrationId(migration.id);
      setStep(3);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al crear la migración");
    }
  }

  const srcName = configs.find((c) => c.id === sourceId)?.name ?? sourceId;
  const dstName = configs.find((c) => c.id === destinationId)?.name ?? destinationId;

  // Barra de progreso
  const progress = migrationStatus
    ? migrationStatus.totalObjects
      ? Math.round((migrationStatus.copiedObjects / migrationStatus.totalObjects) * 100)
      : 0
    : 0;

  const statusColor: Record<string, string> = {
    PENDING:   "text-yellow-600 dark:text-yellow-400",
    RUNNING:   "text-blue-600 dark:text-blue-400",
    DONE:      "text-green-600 dark:text-green-400",
    FAILED:    "text-red-600 dark:text-red-400",
    CANCELLED: "text-gray-500",
  };

  return (
    <Modal
      title={`Nueva migración S3 — Paso ${step} de 3`}
      open
      onClose={onClose}
    >
      {/* Indicador de pasos */}
      <div className="flex items-center gap-2 mb-6">
        {([1, 2, 3] as WizardStep[]).map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
              ${step === s ? "bg-blue-600 text-white" : step > s ? "bg-green-500 text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-500"}`}>
              {step > s ? "✓" : s}
            </div>
            {s < 3 && <div className={`h-px w-8 ${step > s ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700"}`} />}
          </div>
        ))}
        <span className="ml-2 text-sm text-gray-500">
          {step === 1 ? "Seleccionar origen y destino" : step === 2 ? "Confirmar" : "Progreso"}
        </span>
      </div>

      {/* ── Paso 1: selección ── */}
      {step === 1 && (
        <div className="space-y-4">
          <Field label="Almacén origen (source)">
            <Select value={sourceId} onChange={(e) => { setSourceId(e.target.value); setCompatResult(null); }}>
              <option value="">— Selecciona source —</option>
              {configs.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.bucket})</option>
              ))}
            </Select>
          </Field>

          <Field label="Almacén destino (destination)">
            <Select value={destinationId} onChange={(e) => { setDestinationId(e.target.value); setCompatResult(null); }}>
              <option value="">— Selecciona destination —</option>
              {configs.filter((c) => c.id !== sourceId).map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.bucket})</option>
              ))}
            </Select>
          </Field>

          <Field label="Alcance">
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={tenantScope === "global"} onChange={() => { setTenantScope("global"); setCompatResult(null); }} />
                <span className="text-sm">Todo el bucket (todos los tenants)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" checked={tenantScope === "tenant"} onChange={() => { setTenantScope("tenant"); setCompatResult(null); }} />
                <span className="text-sm">Tenant específico</span>
              </label>
            </div>
              {tenantScope === "tenant" && (
              <Select
                className="mt-2"
                value={tenantId}
                onChange={(e) => { setTenantId(e.target.value); setCompatResult(null); }}
              >
                <option value="">— Selecciona un tenant —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.cif ? ` (${t.cif})` : ""}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {/* Botón probar compatibilidad */}
          <div className="flex items-start gap-3">
            <Button
              type="button"
              variant="quiet"
              disabled={!sourceId || !destinationId || testCompatMutation.isPending}
              onClick={handleTestCompat}
            >
              {testCompatMutation.isPending ? "Probando…" : "🔍 Probar compatibilidad"}
            </Button>
          </div>

          {/* Resultado del test */}
          {compatResult && (
            <div className={`rounded-lg border p-3 text-sm space-y-1 ${compatResult.ok ? "border-green-300 bg-green-50 dark:bg-green-900/20" : "border-red-300 bg-red-50 dark:bg-red-900/20"}`}>
              <p className={`font-medium ${compatResult.ok ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"}`}>
                {compatResult.ok ? "✅ Compatible" : "❌ Problema detectado"}
              </p>
              <p className="text-gray-600 dark:text-gray-400">{compatResult.message}</p>
              <div className="flex gap-4 text-xs text-gray-500 pt-1">
                <span>Source: {compatResult.sourceReachable ? "✅" : "❌"}</span>
                <span>Destination: {compatResult.destinationReachable ? "✅" : "❌"}</span>
                {compatResult.estimatedObjects !== null && (
                  <span>Objetos en scope: <strong>{compatResult.estimatedObjects.toLocaleString()}</strong></span>
                )}
              </div>
              {/* Aviso 0 objetos: no es un error, pero conviene informar */}
              {compatResult.ok && compatResult.estimatedObjects === 0 && (
                <p className="text-amber-600 dark:text-amber-400 text-xs pt-1">
                  ℹ️ No hay objetos que copiar en este scope (el tenant aún no tiene DeCA emitidos).
                  La migración se completará inmediatamente y actualizará el almacén asignado al tenant.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="quiet" type="button" onClick={onClose}>Cancelar</Button>
            <Button
              variant="primary"
              type="button"
              disabled={!sourceId || !destinationId || !compatResult?.ok || (tenantScope === "tenant" && !tenantId)}
              onClick={() => setStep(2)}
            >
              Siguiente →
            </Button>
          </div>
        </div>
      )}

      {/* ── Paso 2: confirmación ── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 text-sm">
            <div className="flex justify-between px-4 py-2">
              <span className="text-gray-500">Origen</span>
              <span className="font-medium">{srcName}</span>
            </div>
            <div className="flex justify-between px-4 py-2">
              <span className="text-gray-500">Destino</span>
              <span className="font-medium">{dstName}</span>
            </div>
            <div className="flex justify-between px-4 py-2">
              <span className="text-gray-500">Alcance</span>
              <span className="font-medium">
                {tenantScope === "global"
                  ? "Todo el bucket"
                  : `Tenant: ${tenants.find((t) => t.id === tenantId)?.name ?? tenantId}`}
              </span>
            </div>
            <div className="flex justify-between px-4 py-2">
              <span className="text-gray-500">Objetos estimados</span>
              <span className="font-medium">{compatResult?.estimatedObjects?.toLocaleString() ?? "—"}</span>
            </div>
            <div className="flex justify-between px-4 py-2">
              <span className="text-gray-500">Borrar origen</span>
              <span className="font-medium text-gray-400">No (Object Lock)</span>
            </div>
          </div>

          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-300">
            ⚠️ La migración se ejecuta en segundo plano. Tras completarse, el <code>s3ConfigId</code> de los tenants afectados se actualizará automáticamente para apuntar al destino. El bucket origen <strong>no se borrará</strong>.
          </div>

          <div className="flex justify-between pt-2">
            <Button variant="quiet" type="button" onClick={() => setStep(1)}>← Atrás</Button>
            <Button
              variant="primary"
              type="button"
              disabled={createMigration.isPending}
              onClick={handleCreate}
            >
              {createMigration.isPending ? "Creando…" : "🚀 Iniciar migración"}
            </Button>
          </div>
        </div>
      )}

      {/* ── Paso 3: progreso ── */}
      {step === 3 && (
        <div className="space-y-5">
          {migrationStatus ? (
            <>
              {/* Badge de estado */}
              <div className="flex items-center gap-3">
                <span className={`text-lg font-bold ${statusColor[migrationStatus.status] ?? "text-gray-600"}`}>
                  {migrationStatus.status === "PENDING"   && "⏳ En cola…"}
                  {migrationStatus.status === "RUNNING"   && "⚙️ Copiando objetos…"}
                  {migrationStatus.status === "DONE"      && "✅ Completada"}
                  {migrationStatus.status === "FAILED"    && "❌ Error"}
                  {migrationStatus.status === "CANCELLED" && "🚫 Cancelada"}
                </span>
              </div>

              {/* Barra de progreso */}
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{migrationStatus.copiedObjects.toLocaleString()} copiados</span>
                  <span>{migrationStatus.totalObjects?.toLocaleString() ?? "…"} total</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${migrationStatus.status === "DONE" ? "bg-green-500" : migrationStatus.status === "FAILED" ? "bg-red-500" : "bg-blue-500"}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-right text-xs text-gray-400 mt-1">{progress}%</p>
              </div>

              {/* Contadores */}
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{migrationStatus.copiedObjects}</p>
                  <p className="text-gray-500 text-xs mt-1">Copiados</p>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3">
                  <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{migrationStatus.skippedObjects}</p>
                  <p className="text-gray-500 text-xs mt-1">Saltados</p>
                </div>
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3">
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">{migrationStatus.errorCount}</p>
                  <p className="text-gray-500 text-xs mt-1">Errores</p>
                </div>
              </div>

              {/* Log de errores (si hay) */}
              {migrationStatus.log && migrationStatus.errorCount > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-red-600 dark:text-red-400 font-medium">Ver log de errores</summary>
                  <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded overflow-auto max-h-40 text-gray-700 dark:text-gray-300">
                    {JSON.stringify(JSON.parse(migrationStatus.log), null, 2)}
                  </pre>
                </details>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p className="text-2xl mb-2">⏳</p>
              <p>Esperando estado de la migración…</p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              variant={migrationStatus?.status === "DONE" || migrationStatus?.status === "FAILED" ? "primary" : "quiet"}
              type="button"
              onClick={onClose}
            >
              {migrationStatus?.status === "DONE" ? "Cerrar" : migrationStatus?.status === "FAILED" ? "Cerrar" : "Cerrar (la migración sigue en segundo plano)"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Lista de migraciones existentes
// ---------------------------------------------------------------------------

function MigrationStatusChip({ status }: { status: S3Migration["status"] }) {
  const map: Record<S3Migration["status"], { tone: "ok" | "warn" | "danger" | "neutral"; label: string }> = {
    PENDING:   { tone: "warn",    label: "En cola" },
    RUNNING:   { tone: "neutral", label: "Ejecutando" },
    DONE:      { tone: "ok",      label: "Completada" },
    FAILED:    { tone: "danger",  label: "Error" },
    CANCELLED: { tone: "neutral", label: "Cancelada" },
  };
  const { tone, label } = map[status] ?? { tone: "neutral", label: status };
  return <Chip tone={tone}>{label}</Chip>;
}

function MigrationsList({ onNew }: { onNew: () => void }) {
  const { data: migrations = [], isLoading } = useS3Migrations();

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Últimas 50 migraciones. El estado se actualiza cada 5 segundos.
        </p>
        <Button variant="primary" onClick={onNew}>
          <IconPlus className="h-4 w-4" />
          Nueva migración
        </Button>
      </div>

      {isLoading && <p className="text-sm text-gray-400 py-8 text-center">Cargando…</p>}

      {!isLoading && migrations.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-3xl mb-3">📦</p>
          <p className="font-medium">No hay migraciones todavía</p>
          <p className="text-sm mt-1">Crea una para mover objetos entre buckets S3</p>
        </div>
      )}

      {migrations.length > 0 && (
        <div className="divide-y divide-gray-100 dark:divide-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {migrations.map((m) => {
            const total = m.totalObjects ?? 0;
            const pct = total > 0 ? Math.round((m.copiedObjects / total) * 100) : 0;
            return (
              <div key={m.id} className="px-4 py-3 bg-white dark:bg-gray-900 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <MigrationStatusChip status={m.status} />
                    <span className="text-sm font-medium truncate">
                      {m.source.name} → {m.destination.name}
                    </span>
                    {m.tenantId && (
                      <span className="text-xs text-gray-400 font-mono truncate">tenant:{m.tenantId.slice(0, 8)}…</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">
                    {new Date(m.createdAt).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </div>

                {/* Barra de progreso */}
                {(m.status === "RUNNING" || m.status === "DONE") && (
                  <div>
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>{m.copiedObjects.toLocaleString()} / {total.toLocaleString()} objetos</span>
                      <span>{pct}%{m.errorCount > 0 ? ` · ${m.errorCount} errores` : ""}</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${m.status === "DONE" ? "bg-green-500" : "bg-blue-500"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )}

                {m.status === "FAILED" && m.errorCount > 0 && (
                  <p className="text-xs text-red-500">{m.errorCount} error(es) — abre la migración para ver el log</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Banners de aviso por objectLockMode
// ---------------------------------------------------------------------------

/**
 * Muestra uno o varios avisos contextuales según los modos de retención
 * presentes en los almacenes configurados.
 *
 * - NONE          → peligro: no hay retención WORM, incumple ley DECA.
 * - S3_PER_OBJECT → informativo: retención por objeto; el platform pone el lock.
 * - BUCKET_RULE   → informativo: retención delegada al bucket (R2 / Cloudflare).
 */
function ObjectLockNotices({ configs }: { configs: S3Config[] }) {
  if (configs.length === 0) return null;

  const modes = new Set(configs.map((c) => c.objectLockMode));

  const noneConfigs   = configs.filter((c) => c.objectLockMode === "NONE");
  const perObjConfigs = configs.filter((c) => c.objectLockMode === "S3_PER_OBJECT");
  const bucketConfigs = configs.filter((c) => c.objectLockMode === "BUCKET_RULE");

  return (
    <div className="space-y-2">
      {/* NONE — riesgo legal */}
      {noneConfigs.length > 0 && (
        <Notice tone="danger">
          <span className="font-semibold">⚠️ Sin retención WORM</span> —{" "}
          {noneConfigs.length === 1
            ? <>el almacén <strong>{noneConfigs[0]?.name}</strong> no tiene retención WORM activa.</>
            : <>{noneConfigs.length} almacenes ({noneConfigs.map((c) => c.name).join(", ")}) no tienen retención WORM.</>
          }{" "}
          Los documentos DeCA pueden borrarse de S3 antes del año mínimo legal. Configura{" "}
          <strong>S3 Object Lock</strong> en el bucket o cambia a un proveedor compatible antes de
          emitir documentos en producción.
        </Notice>
      )}

      {/* S3_PER_OBJECT — correcto, pero recordatorio */}
      {perObjConfigs.length > 0 && (
        <Notice tone="neutral">
          <span className="font-semibold">🔒 Retención por objeto (S3 Object Lock)</span> —{" "}
          {perObjConfigs.length === 1
            ? <>almacén <strong>{perObjConfigs[0]?.name}</strong>:</>
            : <>{perObjConfigs.length} almacenes ({perObjConfigs.map((c) => c.name).join(", ")}):</>
          }{" "}
          la plataforma aplica <code>PutObjectRetention</code> (COMPLIANCE, 366 días) en cada PDF al
          subirlo. El bucket debe tener Object Lock habilitado y Versioning activo. Las claves de PDFs
          borrados antes del vencimiento quedan registradas en auditoría como huérfanas.
        </Notice>
      )}

      {/* BUCKET_RULE — correcto, recordatorio de responsabilidad del operador */}
      {bucketConfigs.length > 0 && (
        <Notice tone="neutral">
          <span className="font-semibold">🛡️ Retención por regla de bucket</span> —{" "}
          {bucketConfigs.length === 1
            ? <>almacén <strong>{bucketConfigs[0]?.name}</strong>:</>
            : <>{bucketConfigs.length} almacenes ({bucketConfigs.map((c) => c.name).join(", ")}):</>
          }{" "}
          la retención mínima de 1 año está delegada a la regla del bucket (típicamente Cloudflare R2
          con Object Lock). La plataforma no emite <code>PutObjectRetention</code> individualmente —
          asegúrate de que la regla esté activa y cubra al menos 366 días.
        </Notice>
      )}

      {/* Si todos los modos están presentes, aviso adicional de heterogeneidad */}
      {modes.size === 3 && (
        <Notice tone="warn">
          <span className="font-semibold">Modos mixtos detectados</span> — tienes almacenes con los
          tres modos de retención simultáneamente. Revisa la asignación de tenants para asegurarte de
          que ninguno de producción esté usando un almacén sin retención (NONE).
        </Notice>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

type Tab = "almacenes" | "migraciones";

export function StoragePage() {
  const [activeTab, setActiveTab] = useState<Tab>("almacenes");
  const [showWizard, setShowWizard] = useState(false);

  const { data: configs = [], isLoading, error } = useS3Configs();
  const createMutation = useCreateS3Config();
  const deleteMutation = useDeleteS3Config();
  const testMutation = useTestS3Connection();

  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<S3Config | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const updateMutation = useUpdateS3Config(editTarget?.id ?? "");

  // Las columnas incluyen el botón Editar → se regeneran si openEdit cambia.
  // Como openEdit es estable (siempre la misma función), no necesita useMemo.
  const columns = buildColumns(openEdit);

  function openCreate() {
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowAccessKey(false);
    setShowSecretKey(false);
    setTestResult(null);
    setShowCreate(true);
  }

  function openEdit(config: S3Config) {
    setForm(formFromConfig(config));
    setFormError(null);
    setShowAccessKey(false);
    setShowSecretKey(false);
    setTestResult(null);
    setEditTarget(config);
  }

  function closeModals() {
    setShowCreate(false);
    setEditTarget(null);
    setFormError(null);
    setTestResult(null);
    testMutation.reset();
  }

  function applyProvider(providerId: string) {
    const preset = PROVIDERS.find((p) => p.id === providerId);
    if (!preset) return;
    setForm((prev) => ({
      ...prev,
      provider: providerId,
      endpoint: preset.endpoint ?? "",
      region: preset.region,
      objectLockMode: preset.objectLockMode,
      hasVersioning: preset.hasVersioning,
    }));
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (form.objectLockMode === "S3_PER_OBJECT" && !form.hasVersioning) {
      setFormError("S3 Object Lock requiere Versioning activo en el bucket.");
      return;
    }

    const input: CreateS3ConfigInput = {
      name: form.name,
      endpoint: form.endpoint.trim() || null,
      region: form.region,
      bucket: form.bucket,
      accessKeyId: form.accessKeyId,
      secretAccessKey: form.secretAccessKey,
      objectLockMode: form.objectLockMode,
      hasVersioning: form.hasVersioning,
    };

    try {
      await createMutation.mutateAsync(input);
      closeModals();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Error al crear el almacén");
    }
  }

  async function handleUpdate(e: FormEvent) {
    e.preventDefault();
    if (!editTarget) return;
    setFormError(null);

    if (form.objectLockMode === "S3_PER_OBJECT" && !form.hasVersioning) {
      setFormError("S3 Object Lock requiere Versioning activo en el bucket.");
      return;
    }

    const input: Record<string, unknown> = {
      name: form.name,
      endpoint: form.endpoint.trim() || null,
      region: form.region,
      bucket: form.bucket,
      objectLockMode: form.objectLockMode,
      hasVersioning: form.hasVersioning,
    };
    // Solo enviar credenciales si el usuario las rellenó
    if (form.accessKeyId)     input.accessKeyId     = form.accessKeyId;
    if (form.secretAccessKey) input.secretAccessKey = form.secretAccessKey;

    try {
      await updateMutation.mutateAsync(input);
      closeModals();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Error al editar el almacén");
    }
  }

  async function handleDelete(config: S3Config) {
    if (!window.confirm(`¿Eliminar el almacén "${config.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteMutation.mutateAsync(config.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al eliminar el almacén");
    }
  }

  if (error) return <ErrorNotice error={error} />;

  const isEditing = Boolean(editTarget);
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <PageHeader
        eyebrow="Plataforma"
        title="Almacenes S3"
        description="Credenciales S3-compatibles asignadas a los tenants. Cada almacén define cómo se aplica la retención WORM (mínimo 1 año por ley DECA)."
        actions={
          activeTab === "almacenes" ? (
            <Button variant="primary" onClick={openCreate}>
              <IconPlus className="h-4 w-4" />
              Nuevo almacén
            </Button>
          ) : null
        }
      />

      {/* Pestañas */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-6">
        {(["almacenes", "migraciones"] as Tab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors capitalize
              ${activeTab === tab
                ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"}`}
          >
            {tab === "almacenes" ? "🗄️ Almacenes" : "🔄 Migraciones"}
          </button>
        ))}
      </div>

      {/* Contenido de la pestaña activa */}
      {activeTab === "almacenes" && (
        <div className="space-y-4">
          {/* Banners de aviso por objectLockMode */}
          <ObjectLockNotices configs={configs} />
          <DataTable
            data={configs}
            columns={columns}
            isLoading={isLoading}
            emptyMessage="No hay almacenes configurados"
          />
        </div>
      )}

      {activeTab === "migraciones" && (
        <MigrationsList onNew={() => setShowWizard(true)} />
      )}

      {showWizard && (
        <MigrationWizard configs={configs} onClose={() => setShowWizard(false)} />
      )}

      {/* Modal crear / editar */}
      <Modal
        title={isEditing ? `Editar almacén: ${editTarget!.name}` : "Nuevo almacén S3"}
        open={showCreate || isEditing}
        onClose={closeModals}
      >
        <form onSubmit={isEditing ? handleUpdate : handleCreate} className="space-y-4">

          {/* Selector de proveedor (solo en creación) */}
          {!isEditing && (
            <Field label="Proveedor (autocompletar)">
              <Select
                value={form.provider}
                onChange={(e) => applyProvider(e.target.value)}
              >
                <option value="">— Selecciona proveedor —</option>
                {PROVIDERS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-gray-500">
                Autocompletará endpoint, región y modo de retención. Todos los campos son editables manualmente.
              </p>
            </Field>
          )}

          <Field label="Nombre descriptivo">
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Storadera producción"
              required
            />
          </Field>

          <Field label="Endpoint (vacío = AWS S3 estándar)">
            <Input
              value={form.endpoint}
              onChange={(e) => setForm((f) => ({ ...f, endpoint: e.target.value }))}
              placeholder="https://s3.storadera.com"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Región">
              <Input
                value={form.region}
                onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                placeholder="eu-central-1"
                required
              />
            </Field>
            <Field label="Bucket">
              <Input
                value={form.bucket}
                onChange={(e) => setForm((f) => ({ ...f, bucket: e.target.value }))}
                placeholder="mi-bucket-deca"
                required
              />
            </Field>
          </div>

          <Field label={isEditing ? "Access Key ID (dejar vacío para no cambiar)" : "Access Key ID"}>
            <div className="relative">
              <Input
                type={showAccessKey ? "text" : "password"}
                value={form.accessKeyId}
                onChange={(e) => setForm((f) => ({ ...f, accessKeyId: e.target.value }))}
                placeholder={isEditing ? "••••••••" : "AKIAIOSFODNN7EXAMPLE"}
                required={!isEditing}
                autoComplete="off"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowAccessKey((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
                title={showAccessKey ? "Ocultar" : "Mostrar"}
              >
                {showAccessKey ? "🙈" : "👁"}
              </button>
            </div>
          </Field>

          <Field label={isEditing ? "Secret Access Key (dejar vacío para no cambiar)" : "Secret Access Key"}>
            <div className="relative">
              <Input
                type={showSecretKey ? "text" : "password"}
                value={form.secretAccessKey}
                onChange={(e) => setForm((f) => ({ ...f, secretAccessKey: e.target.value }))}
                placeholder={isEditing ? "••••••••" : "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"}
                required={!isEditing}
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSecretKey((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-400 hover:text-ink-700 dark:hover:text-ink-200"
                title={showSecretKey ? "Ocultar" : "Mostrar"}
              >
                {showSecretKey ? "🙈" : "👁"}
              </button>
            </div>
          </Field>

          {/* Botón Probar conexión (solo en edición — necesita credenciales guardadas) */}
          {isEditing && (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="quiet"
                disabled={testMutation.isPending}
                onClick={() => {
                  setTestResult(null);
                  testMutation.mutate(editTarget!.id, {
                    onSuccess: (result) => setTestResult(result),
                    onError: (err) => setTestResult({ ok: false, message: err instanceof Error ? err.message : "Error desconocido" }),
                  });
                }}
              >
                {testMutation.isPending ? "Probando…" : "🔌 Probar conexión"}
              </Button>
              {testResult && (
                <span className={`text-sm font-medium ${testResult.ok ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                  {testResult.ok ? "✅" : "❌"} {testResult.message}
                </span>
              )}
            </div>
          )}

          <Field label="Retención WORM">
            <Select
              value={form.objectLockMode}
              onChange={(e) => setForm((f) => ({ ...f, objectLockMode: e.target.value as ObjectLockMode }))}
            >
              {(Object.entries(OBJECT_LOCK_LABELS) as [ObjectLockMode, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <div className="mt-2 text-xs text-gray-500 space-y-1">
              {form.objectLockMode === "NONE" && (
                <p className="text-amber-600 font-medium">
                  ⚠️ Sin retención configurada. La normativa DECA exige conservar los documentos mínimo 1 año.
                  El administrador asume la responsabilidad legal.
                </p>
              )}
              {form.objectLockMode === "S3_PER_OBJECT" && (
                <p>La app llama a PutObjectRetention (1 año) en cada PDF. El bucket debe haber sido creado con Object Lock habilitado y Versioning activo.</p>
              )}
              {form.objectLockMode === "BUCKET_RULE" && (
                <p>El proveedor (ej. Cloudflare R2) gestiona la retención mediante Bucket Lock Rules configuradas por el administrador antes de conectar el bucket.</p>
              )}
            </div>
          </Field>

          <Field label="Versioning activo en el bucket">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.hasVersioning}
                onChange={(e) => setForm((f) => ({ ...f, hasVersioning: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Versioning habilitado</span>
            </label>
            {form.objectLockMode === "S3_PER_OBJECT" && !form.hasVersioning && (
              <p className="mt-1 text-xs text-red-500">
                Obligatorio cuando la retención es "Por objeto (S3 Object Lock)".
              </p>
            )}
          </Field>

          {formError && <ErrorNotice error={new Error(formError)} />}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="quiet" type="button" onClick={closeModals}>
              Cancelar
            </Button>
            {isEditing && !editTarget!.isDefault && (
              <Button
                variant="danger"
                type="button"
                onClick={() => { closeModals(); void handleDelete(editTarget!); }}
                disabled={editTarget!._count.tenants > 0}
                title={editTarget!._count.tenants > 0 ? "Tiene tenants asignados" : ""}
              >
                Eliminar
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? "Guardando…" : isEditing ? "Guardar cambios" : "Crear almacén"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
